import React, { useRef } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import { useFlag } from '@context/FeatureToggle'
import { FeatureToggles } from '@enums'
import { IApplicationState } from '@store'
import { selectCampaign } from '@store/campaigns'
import {
  attachNotes,
  attachTranscript,
  detachNotes,
  detachTranscript,
  startDiscoveryRun,
} from '@store/discoveryRuns/actions'
import {
  selectAttachingNotes,
  selectAttachingTranscript,
  selectCanAttachNotes,
  selectCanAttachTranscript,
  selectCanDetachNotes,
  selectCanDetachTranscript,
  selectCanStartDiscoveryRun,
  selectDetachingNotes,
  selectDetachingTranscript,
  selectSourcingBlock,
  selectStartingDiscoveryRun,
} from '@store/discoveryRuns/selectors'
import {
  DiscoveryChannel,
  DiscoveryChannelCounts,
  SimilarHandles,
} from '@store/discoveryRuns/types'
import { Icon } from '@tribegroup'
import {
  CHANNEL_DEFAULT_COUNTS,
  CHANNEL_GLYPHS,
  CHANNEL_LABEL_KEYS,
  CHANNEL_MAX_COUNTS,
  CHANNEL_MIN_COUNT,
  CHANNEL_ORDER,
} from './channelDefaults'
import {
  AttachRow,
  AttachRowActions,
  AttachRowButton,
  AttachRowHint,
  AttachRowLabel,
  AttachRowMain,
  ChannelDisabledHint,
  ChannelName,
  ChannelRow,
  ChannelRowMain,
  ChannelSpacer,
  CloseButton,
  FieldCaption,
  FormCard,
  FormHeading,
  FormSubtitle,
  GenerateCriteriaLabel,
  GenerateCriteriaRow,
  HiddenFileInput,
  NotesTextArea,
  RemoveAffordance,
  ReplaceAffordance,
  RunsUsedNote,
  SectionLabel,
  SimilarHandleAt,
  SimilarHandleError,
  SimilarHandleHint,
  SimilarHandleInput,
  SimilarHandleInputWrap,
  SimilarHandleLabel,
  SimilarHandleOptional,
  SimilarHandleRow,
  StartButton,
  StartLockNote,
  StartRow,
  StepperButton,
  StepperCount,
  StepperGroup,
  StepperUnit,
} from './DiscoveryRunForm.styled'
import Switch from './Switch'

interface IProps {
  campaignId: number
  // Set only for the BRAND-9307 retry/rerun reveal (RunTabs' "Retry run" /
  // "+ New run", link-driven) — renders a close (X) affordance so the user
  // can dismiss the panel without submitting. Omitted for the zero-state
  // mount (AgentSourcedCreatorListView), which stays NOT dismissable per the
  // component doc below.
  onClose?: () => void
  // BRAND-9515 fix (fresh audit finding): notes text + per-channel
  // toggle/count state used to live here as local useState, so a run-pill
  // click (RunTabs' onRunSelect -> setManualRunFormOpen(false)) UNMOUNTED
  // this component and silently discarded whatever the user had typed or
  // adjusted before ever attaching/starting. Both are now lifted to
  // AgentSourcedCreatorListView (the component that owns manualRunFormOpen)
  // as one "draft run config", passed down as value+onChange — the form
  // still closes on a pill click, but the draft survives a reopen via
  // "+ New run" / Retry. channelState is null until first initialized (see
  // initChannelState below); the parent resets both fields to blank/null
  // once a run actually starts (existing sourcingRuns.length-driven effect
  // — the notes/config were CONSUMED by that start).
  notesText: string
  onNotesTextChange: (text: string) => void
  channelState: IChannelState | null
  onChannelStateChange: (next: IChannelState) => void
  // BRAND-9663 (BRAND-9605 tier 2): "Generate Criteria" draft state, lifted
  // to the parent for the same reason notesText/channelState are (see the
  // doc above) — a run-pill click must not silently discard a checked box
  // or a picked file. Hidden entirely on run 1 (see hasRunHistory below) and
  // behind the generateRerunCriteriaEnabled toggle, so these props are
  // inert whenever either gate is closed.
  generateCriteria: boolean
  onGenerateCriteriaChange: (next: boolean) => void
  // A freshly picked file held as a local draft — never attached
  // immediately while attach/detach must stay local (see draftAttachMode)
  // — only reaching the server bundled with the start call itself.
  transcriptFile: File | null
  onTranscriptFileChange: (file: File | null) => void
  noteFile: File | null
  onNoteFileChange: (file: File | null) => void
  // Unlike transcript (required, always falls back to the brief's existing
  // one), note is optional and must be fully clearable — see
  // noteHiddenByClear's doc.
  noteCleared: boolean
  onNoteClearedChange: (cleared: boolean) => void
  // BRAND-9688: draft text for the YouTube "find channels similar to
  // @handle" input — lifted to the parent for the same run-pill-click
  // reason every other draft field here is (see the BRAND-9515 doc above).
  // Raw, untrimmed, with or without a leading "@"; onStartDiscovery
  // normalises it when building the payload. Rendered only behind
  // youTubeSimilarSearchEnabled and only on the YouTube row.
  youtubeSimilarHandle: string
  onYoutubeSimilarHandleChange: (next: string) => void
}

// BRAND-9702: exact mirror of CSA's own _BARE_HANDLE
// (TRIBE-Creator-Sourcing-Agent shared/lookalike_seeds.py) — letters,
// digits, dots, underscores, hyphens, 1-30 chars. Anything else (a URL's
// "/", ":", "?", "#", whitespace, or an over-length string) is not a handle.
const BARE_YOUTUBE_HANDLE_PATTERN = /^[A-Za-z0-9._-]{1,30}$/

export type IChannelState = Record<DiscoveryChannel, { enabled: boolean; count: number }>

export const initChannelState = (channelsInBrief: Set<DiscoveryChannel>): IChannelState =>
  CHANNEL_ORDER.reduce((acc, channel) => {
    acc[channel] = {
      enabled: channelsInBrief.has(channel),
      count: CHANNEL_DEFAULT_COUNTS[channel],
    }
    return acc
  }, {} as IChannelState)

/**
 * Inline first-run discovery setup form (BRAND-9307), rendered on the Vibe
 * Check page's zero-state (see selectIsFirstRunSetupState) in place of the
 * normal run-aware listing. NOT dismissable, no separate page/nav — this
 * exact card IS the page's main content until the first run starts.
 *
 * The sourcing block + attach/start rels arrive on the existing brief-view
 * fetch (already triggered by CampaignProvider before this ever renders —
 * selectIsFirstRunSetupState only goes true once that's loaded), so this
 * component reads store state only; it never dispatches its own fetch.
 *
 * Reused, unchanged, as the BRAND-9307 retry/rerun panel: RunTabs reveals
 * this exact component (passing `onClose`) once "Retry run" / "+ New run"
 * is clicked while the campaign carries `start_discovery_run` — the
 * transcript/notes chips already read as attached from the sourcing block,
 * which IS the "pre-fill" (per the Idea-2 prototype's shot 07). The submit
 * path (onStartDiscovery below) needs no changes: same start_discovery_run
 * link either way.
 */
const DiscoveryRunForm: React.FC<IProps> = ({
  campaignId,
  onClose,
  notesText,
  onNotesTextChange,
  channelState: channelStateProp,
  onChannelStateChange,
  generateCriteria,
  onGenerateCriteriaChange,
  transcriptFile,
  onTranscriptFileChange,
  noteFile,
  onNoteFileChange,
  noteCleared,
  onNoteClearedChange,
  youtubeSimilarHandle,
  onYoutubeSimilarHandleChange,
}) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const generateCriteriaToggleEnabled = useFlag(FeatureToggles.GENERATE_RERUN_CRITERIA_ENABLED)
  const youtubeSimilarSearchEnabled = useFlag(FeatureToggles.YOUTUBE_SIMILAR_SEARCH_ENABLED)

  const campaign = useSelector((state: IApplicationState) => selectCampaign(state, campaignId))
  const sourcingBlock = useSelector((state: IApplicationState) =>
    selectSourcingBlock(state, campaignId),
  )
  const canAttachTranscript = useSelector((state: IApplicationState) =>
    selectCanAttachTranscript(state, campaignId),
  )
  const canAttachNotes = useSelector((state: IApplicationState) =>
    selectCanAttachNotes(state, campaignId),
  )
  // BRAND-9582 item 13 (FINAL fix): the detach rel is the single source of
  // truth for rendering Remove — see selectCanDetachTranscript/Notes' doc.
  const canDetachTranscript = useSelector((state: IApplicationState) =>
    selectCanDetachTranscript(state, campaignId),
  )
  const canDetachNotes = useSelector((state: IApplicationState) =>
    selectCanDetachNotes(state, campaignId),
  )
  const canStartDiscoveryRun = useSelector((state: IApplicationState) =>
    selectCanStartDiscoveryRun(state, campaignId),
  )
  const attachingTranscript = useSelector(selectAttachingTranscript)
  const attachingNotes = useSelector(selectAttachingNotes)
  const detachingTranscript = useSelector(selectDetachingTranscript)
  const detachingNotes = useSelector(selectDetachingNotes)
  const starting = useSelector(selectStartingDiscoveryRun)
  // Review fix: while ANY mutation is in flight (attach, detach, or start)
  // the whole form is inert — prevents interleaved requests and a
  // double-start.
  const anyMutationInFlight =
    attachingTranscript || attachingNotes || detachingTranscript || detachingNotes || starting

  const runsUsed = sourcingBlock?.runs_used
  const maxSourcingRuns = sourcingBlock?.max_sourcing_runs

  const sourcingRuns = sourcingBlock?.sourcing_runs ?? []

  // BRAND-9511 rerun mode: once ANY run has been ATTEMPTED, this same inline
  // form renders as the "new discovery run" setup — rerun copy (heading,
  // subtitle, transcript lock). Keyed off run HISTORY, never runs_used: the
  // BE excludes failed runs from runs_used (they don't consume the budget),
  // so a brief whose only run(s) failed has runs_used: 0 while a "Run 1 ·
  // FAILED" pill is visible right above this form — first-run copy there
  // would be self-contradictory. Exact complement of the zero-state gate
  // (selectIsFirstRunSetupState, sourcing_runs.length === 0).
  //
  // BRAND-9582 item 2 follow-up (Ted's ruling): run history no longer
  // influences the CHANNEL steppers — see the channelState assignment below,
  // which now always initialises from the static defaults regardless of
  // hasRunHistory.
  const hasRunHistory = sourcingRuns.length > 0

  // BRAND-9663 (BRAND-9605 tier 2): the checkbox only ever exists for a
  // rerun (hasRunHistory) behind its own toggle — never on run 1 (nothing
  // to reuse yet) and never when the toggle is off (existing behavior).
  // `effective` additionally guards against a stale `true` surviving a
  // toggle flip mid-session (e.g. reopened from a lifted draft).
  const showGenerateCriteriaOption = hasRunHistory && generateCriteriaToggleEnabled
  const effectiveGenerateCriteria = showGenerateCriteriaOption && generateCriteria
  // Attach/detach stays purely local until Start whenever the toggle is on
  // — either a checked rerun (effectiveGenerateCriteria), or run 1 (which
  // never shows the checkbox at all, since there's nothing to reuse yet).
  const draftAttachMode =
    effectiveGenerateCriteria || (generateCriteriaToggleEnabled && !hasRunHistory)

  // A rerun with the checkbox visible but UNCHECKED is genuinely read-only
  // — no attach, no replace, no remove, view only.
  const readOnlyLocked = showGenerateCriteriaOption && !generateCriteria
  // The original BRAND-9515/9582 rel-driven attach/replace/detach flow — the
  // one state where a click legitimately calls the BE immediately — applies
  // only when the checkbox isn't part of the picture at all (toggle off, on
  // either run 1 or a rerun) and we're not in draftAttachMode either. Every
  // Replace/real-detach affordance below is gated on this rather than on
  // effectiveGenerateCriteria/showGenerateCriteriaOption individually, so it
  // can't leak into readOnlyLocked or draftAttachMode.
  const legacyAttachBehavior = !showGenerateCriteriaOption && !draftAttachMode

  const transcriptAttached = Boolean(sourcingBlock?.transcript_attached)
  const transcriptFilename = sourcingBlock?.transcript_filename
  const notesAttached = Boolean(sourcingBlock?.note_attached)
  const notesFilename = sourcingBlock?.note_filename
  // noteCleared only means anything WHILE draftAttachMode is active —
  // gating it here means unchecking the box (or a legacy/toggle-off view)
  // always reverts to showing genuine server truth, never a stale "cleared"
  // flag left over from an earlier draft session.
  const noteHiddenByClear = draftAttachMode && noteCleared
  // BRAND-9608: once locked (attached), show the server's actual note text
  // instead of the caller's local draft state, which stays blank across a
  // rerun (the draft is reset to '' whenever sourcingRuns.length changes —
  // see AgentSourcedCreatorListView's draftRunConfig effect). Applies
  // whether or not "Generate Criteria" is checked, letting a brand edit
  // just one field without losing the other — onStartDiscovery reads this
  // same value (not the raw draft) so an untouched prefill still gets
  // submitted. Suppressed once explicitly removed (noteHiddenByClear): note
  // is optional, so "removed" means genuinely blank, never silently
  // re-copied from the brief. Typing anything overrides this immediately.
  const notesValue =
    notesAttached && !noteHiddenByClear && !notesText ? sourcingBlock?.note ?? '' : notesText

  // BRAND-9582 item 13 (FINAL fix): Remove dispatches a real detach PUT;
  // transcriptAttached/notesAttached (read straight off the sourcing block)
  // are therefore always server-truth — no local "shown as attached"
  // override remains.

  // BRAND-9515 (Ted's UX call): a transcript/note can be changed as long as
  // discovery run hasn't begun yet. Link-driven, no new client-side state —
  // the BE keeps offering attach_transcript/attach_notes until a run starts
  // (TRIBE-Platform #5358), so "replaceable" is simply attached AND the rel
  // still being present, same pattern as selectCanStartDiscoveryRun.
  //
  // BRAND-9511 fix: the rel is the SINGLE source of truth — no rerun-mode
  // override. The BE drops attach_transcript once a non-failed run exists
  // (the transcript was consumed and is reused for every later run → row
  // freezes with the "Attached once" caption below) and RE-ISSUES it while
  // every run so far has failed (the transcript stays replaceable on
  // retry). A client-side force-lock keyed on run state contradicted the
  // BE's authority in the all-failed case.
  const transcriptReplaceable = transcriptAttached && canAttachTranscript
  // Notes stay purely link-driven too: the start saga itself still attaches
  // pending free-text notes via attach_notes when the BE offers it, so the
  // FE must not pretend notes are frozen while the rel says otherwise.
  const notesReplaceable = notesAttached && canAttachNotes

  // Replace/Remove availability, named once so the wrapping condition below
  // and each affordance's own condition don't repeat the same expression.
  // legacyAttachBehavior and draftAttachMode are mutually exclusive, so
  // Remove only ever resolves to one of the two real actions.
  const transcriptReplaceAvailable = legacyAttachBehavior && transcriptReplaceable
  const transcriptRemoveAvailable =
    (legacyAttachBehavior && canDetachTranscript) || (draftAttachMode && Boolean(transcriptFile))
  const notesReplaceAvailable = legacyAttachBehavior && notesReplaceable
  const notesRemoveAvailable =
    (legacyAttachBehavior && canDetachNotes) ||
    (draftAttachMode && (Boolean(noteFile) || (notesAttached && !noteCleared)))

  const transcriptControlsDisabled =
    anyMutationInFlight || readOnlyLocked || (!draftAttachMode && !canAttachTranscript)
  const notesControlsDisabled =
    anyMutationInFlight || readOnlyLocked || (!draftAttachMode && !canAttachNotes)

  const channelsInBrief = new Set(
    (campaign?.channels ?? [])
      .map((channel) => channel.name as unknown as DiscoveryChannel)
      .filter((name) => (CHANNEL_ORDER as string[]).includes(name)),
  )

  // BRAND-9515 fix: channelState (like notesText) is now owned by the parent
  // (see IProps doc above) so it survives a close/reopen. The campaign is
  // guaranteed loaded before this component ever mounts (selectIsFirstRunSetupState
  // requires the sourcing block, which arrives on the same fetch), so it's
  // safe to fall back to a fresh default derived from `campaign.channels`
  // whenever the parent hasn't initialized its draft yet (channelStateProp
  // is null on the very first mount ever, for this campaign).
  //
  // BRAND-9582 item 2 follow-up (Ted's ruling, reverses BRAND-9511): a fresh
  // draft ALWAYS initialises from the static defaults in channelDefaults.ts
  // — every brief-supported channel enabled at its fixed starting count
  // (TikTok 8, Instagram 6) — never from run history. The BRAND-9511 prefill
  // this replaced walked sourcing_runs for requested_channel_counts /
  // channel_counts and, on real QA data, rendered "1" for every channel
  // because legacy runs carry no requested counts and fall through to
  // imported counts — precisely the defect this reverts. A draft the user
  // has already touched in this session (channelStateProp) still wins; only
  // what a brand-new draft initialises to has changed.
  const channelState = channelStateProp ?? initChannelState(channelsInBrief)

  const transcriptInputRef = useRef<HTMLInputElement>(null)
  const notesInputRef = useRef<HTMLInputElement>(null)

  // With "Generate Criteria" checked (or run 1 with the toggle on), a picked
  // file is held as a local draft instead of attaching immediately — it
  // only reaches the server bundled with the start call itself (see
  // IStartDiscoveryRunPayload's doc for why an immediate attach here would
  // reopen the "attach then reconsider" race).
  const onTranscriptFileSelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || anyMutationInFlight || readOnlyLocked) return
    if (draftAttachMode) {
      onTranscriptFileChange(file)
      return
    }
    dispatch(attachTranscript({ campaignId, file }))
  }

  const onNotesFileSelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || anyMutationInFlight || readOnlyLocked) return
    if (draftAttachMode) {
      onNoteFileChange(file)
      // A fresh pick always supersedes a prior "removed" state.
      if (noteCleared) onNoteClearedChange(false)
      return
    }
    dispatch(attachNotes({ campaignId, file }))
  }

  // BRAND-9582 item 13: dispatches the real detach mutation, guarded
  // defensively even though the button is already disabled in flight. Only
  // ever reachable under legacyAttachBehavior — see its doc above.
  const onRemoveTranscript = (): void => {
    if (anyMutationInFlight || !legacyAttachBehavior) return
    dispatch(detachTranscript({ campaignId }))
  }

  const onRemoveNotes = (): void => {
    if (anyMutationInFlight || !legacyAttachBehavior) return
    dispatch(detachNotes({ campaignId }))
  }

  // Clears the note entirely — pure local state, no dispatch, since draft
  // mode never touches the backend before Start. Unlike transcript, note is
  // optional: this always goes fully blank (clearing both a fresh pick and
  // the "show the brief's existing note" fallback), never reverting to the
  // brief's copy — which means the raw typed draft must be blanked too, not
  // just the file, or a previously-typed note would still be read back by
  // notesValue and resubmitted on Start despite the row showing "removed".
  const onClearDraftNoteFile = (): void => {
    if (anyMutationInFlight) return
    onNoteFileChange(null)
    onNoteClearedChange(true)
    onNotesTextChange('')
  }

  // Mirrors onClearDraftNoteFile for the transcript's own local draft.
  const onClearDraftTranscriptFile = (): void => {
    if (anyMutationInFlight) return
    onTranscriptFileChange(null)
  }

  const toggleChannel = (channel: DiscoveryChannel): void => {
    if (!channelsInBrief.has(channel) || anyMutationInFlight) return
    onChannelStateChange({
      ...channelState,
      [channel]: { ...channelState[channel], enabled: !channelState[channel].enabled },
    })
  }

  const adjustCount = (channel: DiscoveryChannel, delta: number): void => {
    const current = channelState[channel]
    // Silent no-op past the cap/floor — no error, no toast, per the
    // confirmed prototype capture.
    const next = Math.min(
      CHANNEL_MAX_COUNTS[channel],
      Math.max(CHANNEL_MIN_COUNT, current.count + delta),
    )
    onChannelStateChange({ ...channelState, [channel]: { ...current, count: next } })
  }

  const enabledChannelEntries = CHANNEL_ORDER.filter(
    (channel) => channelsInBrief.has(channel) && channelState[channel].enabled,
  )
  const hasValidChannelSelection = enabledChannelEntries.some(
    (channel) => channelState[channel].count >= CHANNEL_MIN_COUNT,
  )
  // Review fix: a brief whose channels don't overlap CHANNEL_ORDER at all
  // would otherwise be a silent dead end (every row disabled, Start forever
  // grey) — render an explanatory line instead.
  const hasAnyDiscoverableChannel = channelsInBrief.size > 0
  // A transcript is required for this run — either a freshly picked file,
  // or the brief's already-attached one (server truth; a rerun always has
  // one, since transcript is mandatory pre-run). transcriptFile only ever
  // has a value while genuinely in draft mode, so this covers the legacy
  // (non-draft) case too without branching.
  const transcriptSatisfied = Boolean(transcriptFile) || transcriptAttached

  // BRAND-9702 (review fix): mirrors CSA's own bare-handle shape check
  // (TRIBE-Creator-Sourcing-Agent shared/lookalike_seeds.py _BARE_HANDLE)
  // exactly, so a pasted channel URL is caught here with visible feedback
  // instead of reaching CSA, which rejects it silently (a log line the
  // brand never sees) and completes the run as if nothing were wrong.
  // Shape-only, independent of whether YouTube is currently enabled, so the
  // error clears the moment the text is fixed rather than only once the
  // channel happens to be toggled.
  const bareSimilarHandle = youtubeSimilarHandle.trim().replace(/^@+/, '')
  const similarHandleShapeInvalid =
    Boolean(bareSimilarHandle) && !BARE_YOUTUBE_HANDLE_PATTERN.test(bareSimilarHandle)
  // Only blocks Start when the bad value would actually be submitted — a
  // URL typed while YouTube itself is toggled off is already silently
  // dropped (onStartDiscovery below), so it shouldn't block an otherwise
  // valid run.
  const similarHandleBlocksSubmit =
    youtubeSimilarSearchEnabled &&
    enabledChannelEntries.includes('youtube') &&
    similarHandleShapeInvalid

  const canSubmit =
    transcriptSatisfied &&
    hasValidChannelSelection &&
    canStartDiscoveryRun &&
    !anyMutationInFlight &&
    !similarHandleBlocksSubmit

  const onStartDiscovery = (): void => {
    if (!canSubmit) return
    // Read from notesValue (the effective, possibly pre-filled display
    // value), not the raw draft state — otherwise an untouched prefill
    // would submit as empty instead of what's actually shown on screen.
    // Used only by the legacy (non-draft) dispatch below.
    const trimmedNotes = notesValue.trim()
    // The atomic bundle sends note TEXT only when the user actually typed
    // something fresh (raw notesText, not the display-only notesValue
    // fallback) — an untouched note is left completely alone server-side,
    // same as transcript.
    const trimmedFreshNote = notesText.trim()
    const channels: DiscoveryChannelCounts = enabledChannelEntries.reduce((acc, channel) => {
      acc[channel] = channelState[channel].count
      return acc
    }, {} as DiscoveryChannelCounts)

    // BRAND-9688: only sent when the flag is on, YouTube is actually enabled
    // this run, and the brand typed something — a disabled channel or an
    // empty/whitespace-only input must not send an empty seed. The input
    // renders its own fixed "@" prefix as a visual hint, but a brand may
    // still type their own leading "@" (or several) regardless — strip
    // whatever they typed, then always prepend exactly one "@" ourselves, so
    // the value sent is always in "@handle" form no matter how it was typed.
    // bareSimilarHandle itself is computed above canSubmit (BRAND-9702) so
    // the same value backs both the Start gate and this payload.
    const normalizedSimilarHandle = bareSimilarHandle ? `@${bareSimilarHandle}` : ''
    const similarHandles: SimilarHandles | undefined =
      youtubeSimilarSearchEnabled &&
      enabledChannelEntries.includes('youtube') &&
      normalizedSimilarHandle
        ? { youtube: normalizedSimilarHandle }
        : undefined

    // BRAND-9663: one atomic dispatch carrying generateCriteria + the fresh
    // transcript/note — the saga routes this to a single multipart request,
    // never through the attach-then-start flow below (see
    // IStartDiscoveryRunPayload's doc for why). canSubmit already
    // guarantees transcriptSatisfied by the time this runs, so
    // transcriptFile/note/noteFile are only ever sent when the user
    // actually replaced/typed/cleared something — otherwise they're
    // omitted entirely and the brief's existing transcript/note is left
    // completely untouched server-side. An explicit removal sends note as
    // an empty string rather than omitting it, since Platform treats an
    // absent `note` as "leave whatever's already there".
    //
    // Entered whenever the toggle is on — draftAttachMode (checked rerun or
    // run 1) OR showGenerateCriteriaOption (a rerun with the checkbox
    // visible, even unchecked) — not just draftAttachMode alone: Platform
    // defaults an ABSENT generate_criteria to true on a rerun, so an
    // unchecked rerun must still go through this dispatch to send
    // generateCriteria: false explicitly, rather than falling through to
    // the legacy dispatch below, which has no generateCriteria field at
    // all and would silently regenerate criteria against the user's intent.
    if (draftAttachMode || showGenerateCriteriaOption) {
      dispatch(
        startDiscoveryRun({
          campaignId,
          channels,
          // undefined only on run 1 (no checkbox exists there at all, so
          // there's nothing to tell Platform); a rerun with the checkbox
          // visible always sends its actual checked state, true or false.
          generateCriteria: showGenerateCriteriaOption ? generateCriteria : undefined,
          transcriptFile: transcriptFile ?? undefined,
          note: trimmedFreshNote ? trimmedFreshNote : noteHiddenByClear ? '' : undefined,
          noteFile: noteFile ?? undefined,
          similarHandles,
        }),
      )
      return
    }

    // Single orchestrated dispatch (review fix): pending free-text notes ride
    // along on startDiscoveryRun — the saga attaches them first, awaits the
    // outcome, then starts, applying ONE final campaign snapshot. Never
    // dispatch attachNotes + startDiscoveryRun concurrently from here.
    //
    // Sent whenever non-empty, regardless of notesAttached (review fix,
    // BRAND-9515 #4): a notes FILE already being attached must not silently
    // drop typed free-text notes — buildNotesFormData supports sending both,
    // and the BE allows replacing an attached note right up until a
    // discovery run has begun (the same window this Start action lives in).
    dispatch(
      startDiscoveryRun({
        campaignId,
        channels,
        notes: trimmedNotes ? trimmedNotes : undefined,
        similarHandles,
      }),
    )
  }

  return (
    <FormCard data-testid="discovery-run-form">
      {onClose && (
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label={intl.formatMessage({ id: 'creatorSearch.discoveryRun.setup.closeLabel' })}
          data-testid="discovery-run-form-close"
        >
          <Icon glyph="close-x" size={1} />
        </CloseButton>
      )}
      <FormHeading>
        <FormattedMessage
          id={
            hasRunHistory
              ? 'creatorSearch.discoveryRun.setup.rerunHeading'
              : 'creatorSearch.discoveryRun.setup.heading'
          }
        />
      </FormHeading>
      <FormSubtitle>
        <FormattedMessage
          id={
            hasRunHistory
              ? 'creatorSearch.discoveryRun.setup.rerunSubtitle'
              : 'creatorSearch.discoveryRun.setup.subtitle'
          }
        />
      </FormSubtitle>
      {/* BRAND-9511: only rendered when the BE sends max_sourcing_runs.
          BRAND-9582 (QA item 6): and only in the zero-state — RunTabs
          renders the same "N of M" line once run history exists, so
          showing both would duplicate it. */}
      {typeof maxSourcingRuns === 'number' && !hasRunHistory && (
        <RunsUsedNote data-testid="runs-used-header">
          <FormattedMessage
            id="creatorSearch.discoveryRun.runsUsed"
            values={{ used: runsUsed ?? 0, max: maxSourcingRuns }}
          />
        </RunsUsedNote>
      )}

      {/* BRAND-9663 (BRAND-9605 tier 2): hidden on run 1 and behind its own
          toggle — see showGenerateCriteriaOption's doc above. Unchecked by
          default; the transcript/notes rows below stay in their normal
          locked-rerun state until this is checked. */}
      {showGenerateCriteriaOption && (
        <GenerateCriteriaRow data-testid="generate-criteria-row">
          <Switch
            on={generateCriteria}
            disabled={anyMutationInFlight}
            onToggle={() => onGenerateCriteriaChange(!generateCriteria)}
            label={intl.formatMessage({ id: 'creatorSearch.discoveryRun.generateCriteria.label' })}
            data-testid="generate-criteria-toggle"
          />
          <GenerateCriteriaLabel>
            <FormattedMessage id="creatorSearch.discoveryRun.generateCriteria.label" />
          </GenerateCriteriaLabel>
        </GenerateCriteriaRow>
      )}
      {showGenerateCriteriaOption && (
        <FieldCaption data-testid="generate-criteria-hint">
          <FormattedMessage id="creatorSearch.discoveryRun.generateCriteria.hint" />
        </FieldCaption>
      )}

      <SectionLabel>
        <FormattedMessage id="creatorSearch.discoveryRun.transcript.label" />
      </SectionLabel>
      <AttachRow
        // The row shows just the filename — a freshly picked draft file
        // wins; otherwise the existing filename (if any) keeps showing even
        // while Generate Criteria is checked, since the brief's existing
        // transcript stays attached server-side untouched unless replaced.
        $attached={Boolean(transcriptFile) || transcriptAttached}
        $disabled={transcriptControlsDisabled}
        data-testid="transcript-attach-row"
      >
        <AttachRowMain>
          <AttachRowButton
            type="button"
            disabled={transcriptControlsDisabled}
            onClick={() => transcriptInputRef.current?.click()}
            data-testid="transcript-attach-button"
          >
            <Icon glyph={transcriptFile || transcriptAttached ? 'document-doc' : 'plus'} size={1} />
            <AttachRowLabel>
              {transcriptFile?.name ??
                (transcriptAttached
                  ? transcriptFilename
                  : intl.formatMessage({ id: 'creatorSearch.discoveryRun.transcript.attachCta' }))}
            </AttachRowLabel>
          </AttachRowButton>
          {/* BRAND-9582 item 13 (Ted design feedback): Replace + Remove
              render together here, right beside the file-pick button, as a
              matched pair. Remove resolves to whichever action is actually
              available — a real detach (legacyAttachBehavior) or clearing
              the local draft (draftAttachMode) — the two never overlap. */}
          {transcriptReplaceAvailable || transcriptRemoveAvailable ? (
            <AttachRowActions>
              {transcriptReplaceAvailable && (
                <ReplaceAffordance
                  onClick={() => transcriptInputRef.current?.click()}
                  data-testid="transcript-replace-affordance"
                >
                  <FormattedMessage id="creatorSearch.discoveryRun.transcript.replaceCta" />
                </ReplaceAffordance>
              )}
              {transcriptRemoveAvailable && (
                <RemoveAffordance
                  type="button"
                  onClick={legacyAttachBehavior ? onRemoveTranscript : onClearDraftTranscriptFile}
                  disabled={anyMutationInFlight}
                  aria-label={intl.formatMessage({
                    id: 'creatorSearch.discoveryRun.transcript.removeLabel',
                  })}
                  data-testid="transcript-remove-button"
                >
                  <FormattedMessage id="creatorSearch.discoveryRun.transcript.removeCta" />
                </RemoveAffordance>
              )}
            </AttachRowActions>
          ) : null}
        </AttachRowMain>
        <AttachRowHint>
          <FormattedMessage
            id={
              effectiveGenerateCriteria
                ? transcriptFile
                  ? 'creatorSearch.discoveryRun.transcript.freshAttachedCaption'
                  : // No fresh file picked, but the brief's existing
                  // transcript (server truth) already satisfies Start —
                  // distinct copy from the "still required" case so it's
                  // clear attaching a new file is optional here.
                  transcriptAttached
                  ? 'creatorSearch.discoveryRun.transcript.freshOptionalCaption'
                  : 'creatorSearch.discoveryRun.transcript.freshRequired'
                : !transcriptAttached
                ? 'creatorSearch.discoveryRun.transcript.required'
                : transcriptReplaceAvailable
                ? 'creatorSearch.discoveryRun.transcript.attachedReplaceableCaption'
                : 'creatorSearch.discoveryRun.transcript.attachedCaption'
            }
          />
        </AttachRowHint>
      </AttachRow>
      <HiddenFileInput
        ref={transcriptInputRef}
        type="file"
        accept=".md"
        onChange={onTranscriptFileSelected}
        data-testid="transcript-file-input"
      />

      <SectionLabel>
        <FormattedMessage id="creatorSearch.discoveryRun.notes.label" />
      </SectionLabel>
      <AttachRow
        $attached={Boolean(noteFile) || (notesAttached && !noteHiddenByClear)}
        $disabled={notesControlsDisabled}
        data-testid="notes-attach-row"
      >
        <AttachRowMain>
          <AttachRowButton
            type="button"
            disabled={notesControlsDisabled}
            onClick={() => notesInputRef.current?.click()}
            data-testid="notes-attach-button"
          >
            <Icon
              glyph={noteFile || (notesAttached && !noteHiddenByClear) ? 'document-doc' : 'plus'}
              size={1}
            />
            <AttachRowLabel>
              {noteFile?.name ??
                (notesAttached && !noteHiddenByClear
                  ? notesFilename ??
                    intl.formatMessage({ id: 'creatorSearch.discoveryRun.notes.attachedCaption' })
                  : intl.formatMessage({ id: 'creatorSearch.discoveryRun.notes.attachCta' }))}
            </AttachRowLabel>
          </AttachRowButton>
          {/* Mirrors the transcript row above. Unlike transcript, Remove is
              available as soon as draft mode is active and there's anything
              to clear — a fresh pick OR the brief's existing note — since
              note must be fully clearable from the very start. */}
          {notesReplaceAvailable || notesRemoveAvailable ? (
            <AttachRowActions>
              {notesReplaceAvailable && (
                <ReplaceAffordance
                  onClick={() => notesInputRef.current?.click()}
                  data-testid="notes-replace-affordance"
                >
                  <FormattedMessage id="creatorSearch.discoveryRun.notes.replaceCta" />
                </ReplaceAffordance>
              )}
              {notesRemoveAvailable && (
                <RemoveAffordance
                  type="button"
                  onClick={legacyAttachBehavior ? onRemoveNotes : onClearDraftNoteFile}
                  disabled={anyMutationInFlight}
                  aria-label={intl.formatMessage({
                    id: 'creatorSearch.discoveryRun.notes.removeLabel',
                  })}
                  data-testid="notes-remove-button"
                >
                  <FormattedMessage id="creatorSearch.discoveryRun.notes.removeCta" />
                </RemoveAffordance>
              )}
            </AttachRowActions>
          ) : null}
        </AttachRowMain>
        <AttachRowHint>
          <FormattedMessage
            id={
              effectiveGenerateCriteria
                ? noteFile
                  ? 'creatorSearch.discoveryRun.notes.freshAttachedCaption'
                  : 'creatorSearch.discoveryRun.notes.freshOptionalCaption'
                : !notesAttached
                ? 'creatorSearch.discoveryRun.notes.optional'
                : notesReplaceAvailable
                ? 'creatorSearch.discoveryRun.notes.attachedReplaceableCaption'
                : 'creatorSearch.discoveryRun.notes.attachedCaption'
            }
          />
        </AttachRowHint>
      </AttachRow>
      <HiddenFileInput
        ref={notesInputRef}
        type="file"
        accept=".md"
        onChange={onNotesFileSelected}
        data-testid="notes-file-input"
      />
      <NotesTextArea
        placeholder={intl.formatMessage({ id: 'creatorSearch.discoveryRun.notes.textPlaceholder' })}
        value={notesValue}
        onChange={(e) => onNotesTextChange(e.target.value)}
        // BRAND-9663: "Generate Criteria" checked always allows typing a
        // fresh note, overriding the normal locked/no-rel disabled states —
        // this is precisely the field it exists to unlock.
        disabled={
          anyMutationInFlight ||
          readOnlyLocked ||
          (!draftAttachMode && (notesAttached || !canAttachNotes))
        }
        data-testid="notes-textarea"
      />
      <FieldCaption data-testid="notes-text-caption">
        <FormattedMessage
          id={
            effectiveGenerateCriteria
              ? 'creatorSearch.discoveryRun.notes.textCaption'
              : // Review fix: attach_notes absent + nothing attached yet means
              // typed text has nowhere to go — the saga drops it and starts
              // anyway, so say so before the user types rather than after.
              !notesAttached && !canAttachNotes
              ? 'creatorSearch.discoveryRun.notes.textLockedCaption'
              : 'creatorSearch.discoveryRun.notes.textCaption'
          }
        />
      </FieldCaption>

      <SectionLabel>
        <FormattedMessage id="creatorSearch.discoveryRun.channels.label" />
      </SectionLabel>
      {CHANNEL_ORDER.map((channel) => {
        const isInBrief = channelsInBrief.has(channel)
        const state = channelState[channel]
        // BRAND-9688: YouTube only, and only once the flag is on — never
        // rendered at all for TikTok/Instagram or while the toggle is off.
        const showSimilarHandleRow =
          channel === 'youtube' && isInBrief && youtubeSimilarSearchEnabled
        const similarHandleDisabled = !state.enabled || anyMutationInFlight
        return (
          <ChannelRow key={channel} $disabled={!isInBrief} data-testid={`channel-row-${channel}`}>
            <ChannelRowMain>
              <Switch
                on={isInBrief && state.enabled}
                disabled={!isInBrief || anyMutationInFlight}
                onToggle={() => toggleChannel(channel)}
                label={intl.formatMessage({ id: CHANNEL_LABEL_KEYS[channel] })}
                data-testid={`channel-toggle-${channel}`}
              />
              <ChannelName>
                <Icon glyph={CHANNEL_GLYPHS[channel]} size={1.25} />
                <FormattedMessage id={CHANNEL_LABEL_KEYS[channel]} />
              </ChannelName>
              <ChannelSpacer />
              {isInBrief ? (
                <StepperGroup>
                  <StepperButton
                    type="button"
                    onClick={() => adjustCount(channel, -1)}
                    disabled={
                      !state.enabled || state.count <= CHANNEL_MIN_COUNT || anyMutationInFlight
                    }
                    data-testid={`channel-decrement-${channel}`}
                  >
                    −
                  </StepperButton>
                  <StepperCount data-testid={`channel-count-${channel}`}>
                    {state.count}
                  </StepperCount>
                  <StepperButton
                    type="button"
                    onClick={() => adjustCount(channel, 1)}
                    disabled={
                      !state.enabled ||
                      state.count >= CHANNEL_MAX_COUNTS[channel] ||
                      anyMutationInFlight
                    }
                    data-testid={`channel-increment-${channel}`}
                  >
                    +
                  </StepperButton>
                  <StepperUnit>
                    <FormattedMessage id="creatorSearch.discoveryRun.channels.unit" />
                  </StepperUnit>
                </StepperGroup>
              ) : (
                <ChannelDisabledHint>
                  <FormattedMessage id="creatorSearch.discoveryRun.channels.notInBrief" />
                </ChannelDisabledHint>
              )}
            </ChannelRowMain>
            {showSimilarHandleRow && (
              <SimilarHandleRow data-testid="youtube-similar-handle-row">
                <SimilarHandleLabel>
                  <FormattedMessage id="creatorSearch.discoveryRun.channels.youtube.similarHandle.label" />
                </SimilarHandleLabel>
                <SimilarHandleInputWrap $disabled={similarHandleDisabled}>
                  <SimilarHandleAt>@</SimilarHandleAt>
                  <SimilarHandleInput
                    type="text"
                    value={youtubeSimilarHandle}
                    onChange={(e) => onYoutubeSimilarHandleChange(e.target.value)}
                    disabled={similarHandleDisabled}
                    placeholder={intl.formatMessage({
                      id: 'creatorSearch.discoveryRun.channels.youtube.similarHandle.placeholder',
                    })}
                    data-testid="youtube-similar-handle-input"
                  />
                  {/* "Optional" only means anything while the field is empty
                      — once the brand has typed a handle it's redundant
                      clutter next to their own input, so it's suppressed as
                      soon as there's a non-whitespace value (matching the
                      same trim-based blank check onStartDiscovery uses to
                      decide whether to send similarHandles at all). */}
                  {!youtubeSimilarHandle.trim() && (
                    <SimilarHandleOptional data-testid="youtube-similar-handle-optional-label">
                      <FormattedMessage id="creatorSearch.discoveryRun.channels.youtube.similarHandle.optional" />
                    </SimilarHandleOptional>
                  )}
                </SimilarHandleInputWrap>
                {/* BRAND-9702: the error replaces the hint entirely (never
                    shown together) — mirrors CSA's own bare-handle shape
                    check so a pasted URL is caught here, visibly, instead of
                    silently dropped server-side. */}
                {similarHandleShapeInvalid ? (
                  <SimilarHandleError data-testid="youtube-similar-handle-error">
                    <FormattedMessage id="creatorSearch.discoveryRun.channels.youtube.similarHandle.invalidHandle" />
                  </SimilarHandleError>
                ) : (
                  <SimilarHandleHint>
                    <FormattedMessage id="creatorSearch.discoveryRun.channels.youtube.similarHandle.hint" />
                  </SimilarHandleHint>
                )}
              </SimilarHandleRow>
            )}
          </ChannelRow>
        )
      })}
      {!hasAnyDiscoverableChannel && (
        <FieldCaption data-testid="channels-none-available">
          <FormattedMessage id="creatorSearch.discoveryRun.channels.noneAvailable" />
        </FieldCaption>
      )}
      <FieldCaption>
        <FormattedMessage id="creatorSearch.discoveryRun.channels.comingSoon" />
      </FieldCaption>

      <StartRow>
        <StartButton
          type="button"
          disabled={!canSubmit}
          onClick={onStartDiscovery}
          data-testid="start-discovery-button"
        >
          <FormattedMessage id="creatorSearch.discoveryRun.start.cta" />
        </StartButton>
        <StartLockNote>
          <FormattedMessage id="creatorSearch.discoveryRun.start.lockNote" />
        </StartLockNote>
      </StartRow>
    </FormCard>
  )
}

export { DiscoveryRunForm }
export default DiscoveryRunForm
